import type { Dayjs } from 'dayjs';
import { normalizeAnalyticsPageIds } from 'app/modules/analytics/utils/normalizeAnalyticsPageIds';
import { queryKey } from 'constants/queryKey';

const ISO_DATE = 'YYYY-MM-DD';

/** Everything an analytics request needs, derived once and passed as a value. */
export interface AnalyticsQueryDescriptor {
  pageIds: number[];
  primaryPageId: number | null;
  since: string;
  until: string;
  compareSince?: string;
  compareUntil?: string;
  timezone: string;
}

export interface AnalyticsPageIdsInput {
  scopedPageIds: number[] | null;
  isGroupSelected: boolean;
  enabledGroupPageIds: ReadonlySet<number>;
  selectedPageId: number | null;
}

export function resolveAnalyticsPageIds(
  input: AnalyticsPageIdsInput,
): number[] {
  const {
    scopedPageIds,
    isGroupSelected,
    enabledGroupPageIds,
    selectedPageId,
  } = input;

  if (scopedPageIds != null && scopedPageIds.length > 0) {
    return normalizeAnalyticsPageIds(scopedPageIds);
  }

  if (isGroupSelected && enabledGroupPageIds.size > 0) {
    return normalizeAnalyticsPageIds(Array.from(enabledGroupPageIds));
  }

  if (selectedPageId != null) {
    return [selectedPageId];
  }

  return [];
}

/** The single page a subtree is about, in source order — not `pageIds[0]`, which is normalised. */
export function resolveAnalyticsPrimaryPageId(
  input: Pick<AnalyticsPageIdsInput, 'scopedPageIds' | 'selectedPageId'>,
): number | null {
  const scopedPrimary = input.scopedPageIds?.[0];
  return scopedPrimary ?? input.selectedPageId;
}

export interface AnalyticsDescriptorInput extends AnalyticsPageIdsInput {
  startDate: Dayjs;
  endDate: Dayjs;
  timezone: string;
  isComparisonEnabled: boolean;
  comparisonStartDate: Dayjs;
  comparisonEndDate: Dayjs;
}

export function buildAnalyticsQueryDescriptor(
  input: AnalyticsDescriptorInput,
): AnalyticsQueryDescriptor {
  return {
    pageIds: resolveAnalyticsPageIds(input),
    primaryPageId: resolveAnalyticsPrimaryPageId(input),
    since: input.startDate.format(ISO_DATE),
    until: input.endDate.format(ISO_DATE),
    compareSince: input.isComparisonEnabled
      ? input.comparisonStartDate.format(ISO_DATE)
      : undefined,
    compareUntil: input.isComparisonEnabled
      ? input.comparisonEndDate.format(ISO_DATE)
      : undefined,
    timezone: input.timezone,
  };
}

export function withAnalyticsPageIds(
  descriptor: AnalyticsQueryDescriptor,
  pageIds: number[],
): AnalyticsQueryDescriptor {
  return { ...descriptor, pageIds: normalizeAnalyticsPageIds(pageIds) };
}

export function hasAnalyticsQueryablePages(
  descriptor: AnalyticsQueryDescriptor,
): boolean {
  return descriptor.pageIds.length > 0;
}

/** Delegates to the existing factories so key identity is preserved exactly. */
export const analyticsDescriptorKey = {
  overview: (descriptor: AnalyticsQueryDescriptor) =>
    queryKey.analyticsOverview({
      pageIds: descriptor.pageIds,
      since: descriptor.since,
      until: descriptor.until,
      compareSince: descriptor.compareSince,
      compareUntil: descriptor.compareUntil,
    }),
  performance: (descriptor: AnalyticsQueryDescriptor) =>
    queryKey.analyticsPerformance(
      descriptor.pageIds,
      descriptor.since,
      descriptor.until,
      descriptor.compareSince,
      descriptor.compareUntil,
    ),
  audience: (descriptor: AnalyticsQueryDescriptor) =>
    queryKey.analyticsAudience(descriptor.pageIds),
  bestTimeToPost: (descriptor: AnalyticsQueryDescriptor) =>
    queryKey.analyticsBestTimeToPost(
      descriptor.pageIds,
      descriptor.since,
      descriptor.until,
    ),
  content: (descriptor: AnalyticsQueryDescriptor, extra?: unknown) =>
    queryKey.analyticsContent(
      descriptor.pageIds,
      descriptor.since,
      descriptor.until,
      extra,
    ),
};
